import { useEffect, useState } from "react";
import { Link } from "react-router";
import { daysLeft, daysLeftLabel, formatTenge, vatLabel, type PaymentTaskRow } from "@omts/shared";
import { api, subscribeEvents } from "../api";
import { useAuth } from "../auth";
import { Modal } from "./Modal";
import { fmtDateTime } from "../time";

const errMsg = (e: unknown) => (e instanceof Error ? e.message : "Неизвестная ошибка");
const fmtDate = new Intl.DateTimeFormat("ru-RU", { timeZone: "Asia/Almaty", day: "2-digit", month: "2-digit", year: "numeric" });

/** Срок подачи на оплату: «осталось N дней» / «просрочено на N дней» с подсветкой (14.3). */
function Due({ dueAt, paid }: { dueAt: string; paid: boolean }) {
  if (paid) return <span className="muted">{fmtDate.format(new Date(dueAt))}</span>;
  const days = daysLeft(dueAt);
  const cls = days < 0 ? "due overdue" : days <= 3 ? "due soon" : "due";
  return (
    <span className={cls}>
      {fmtDate.format(new Date(dueAt))}
      <small>{daysLeftLabel(days)}</small>
    </span>
  );
}

function PaidModal({ row, onClose, onDone }: { row: PaymentTaskRow; onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.markPaid(row.id);
      onDone();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Отметить оплату по ${row.lotNumber}`} onClose={onClose}>
      <p>
        Перевозчик <b>{row.carrierName ?? "—"}</b>, сумма{" "}
        <b>
          {row.amount === null ? "—" : formatTenge(row.amount)} {vatLabel(row.transportKind)}
        </b>
        . Заявка перейдёт в статус «Оплачено», задача уйдёт в архив.
      </p>
      {error && <p className="error">{error}</p>}
      <div className="modal-actions">
        <button type="button" className="btn" onClick={onClose} disabled={busy}>
          Отмена
        </button>
        <button type="button" className="btn primary" onClick={() => void confirm()} disabled={busy}>
          {busy ? "Сохраняем…" : "Оплачено"}
        </button>
      </div>
    </Modal>
  );
}

/** Блок «К оплате» на главной: открытые задачи и архив оплаченных (раздел 14.3). */
export function PaymentTasks() {
  const { user } = useAuth();
  const [tab, setTab] = useState<"open" | "paid">("open");
  const [onlyMine, setOnlyMine] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState<PaymentTaskRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState<PaymentTaskRow | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const load = () =>
    api
      .paymentTasks(tab, tab === "paid" ? { from: from || undefined, to: to ? `${to}T23:59:59.999Z` : undefined } : undefined)
      .then((r) => {
        setRows(r);
        setError(null);
      })
      .catch((e: unknown) => setError(errMsg(e)));

  useEffect(() => {
    void load();
    return subscribeEvents((type) => {
      if (type === "lot.updated") void load();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, from, to]);

  const visible = (rows ?? []).filter((r) => !onlyMine || r.lotCreatedBy === user?.id);
  const overdue = (rows ?? []).filter((r) => !r.paidAt && daysLeft(r.dueAt) < 0).length;

  return (
    <section className="card payment-tasks">
      <div className="row between" style={{ marginBottom: collapsed ? 0 : 10 }}>
        <h3 style={{ margin: 0 }}>
          К оплате{" "}
          {rows && tab === "open" && (
            <span className="muted">
              ({rows.length}
              {overdue > 0 && <span className="overdue-count"> · просрочено {overdue}</span>})
            </span>
          )}
        </h3>
        <div className="row" style={{ gap: 14 }}>
          <div className="seg">
            <button type="button" className={tab === "open" ? "active" : ""} onClick={() => setTab("open")}>
              Открытые
            </button>
            <button type="button" className={tab === "paid" ? "active" : ""} onClick={() => setTab("paid")}>
              Оплаченные
            </button>
          </div>
          <label className="muted" style={{ fontSize: 14 }}>
            <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} /> только мои
          </label>
          {tab === "paid" && (
            <span className="row period">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} aria-label="с" />
              <span className="muted">—</span>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} aria-label="по" />
            </span>
          )}
          <button type="button" className="btn small" onClick={() => setCollapsed((c) => !c)}>
            {collapsed ? "Развернуть" : "Свернуть"}
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {error && <p className="error">{error}</p>}
          {rows && visible.length === 0 && !error && (
            <p className="muted" style={{ margin: 0 }}>
              {tab === "open" ? "Подтверждённых доставок, ожидающих оплаты, нет." : "Оплаченных заявок за период нет."}
            </p>
          )}
          {visible.length > 0 && (
            <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Лот</th>
                  <th>Перевозчик</th>
                  <th>Сумма</th>
                  <th>Подтверждено</th>
                  <th>{tab === "open" ? "Срок оплаты" : "Оплачено"}</th>
                  <th>Автор лота</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.id} className={!r.paidAt && daysLeft(r.dueAt) < 0 ? "overdue" : ""}>
                    <td className="nowrap">
                      <Link to={`/lots/${r.lotId}`} className="lot-link">
                        <b>{r.lotNumber}</b>
                      </Link>
                      <div className="sub">
                        {r.loadingAddress} → {r.unloadingAddress}
                      </div>
                    </td>
                    <td>{r.carrierName ?? "—"}</td>
                    <td className="amount">
                      {r.amount === null ? "—" : formatTenge(r.amount)} <span className="muted">{vatLabel(r.transportKind)}</span>
                    </td>
                    <td className="nowrap">
                      {fmtDateTime(r.confirmedAt)}
                      <div className="sub">{r.confirmedByName}</div>
                    </td>
                    <td className="nowrap">
                      {tab === "open" ? (
                        <Due dueAt={r.dueAt} paid={false} />
                      ) : (
                        <>
                          {fmtDateTime(r.paidAt)}
                          <div className="sub">{r.paidByName ?? ""}</div>
                        </>
                      )}
                    </td>
                    <td>{r.lotCreatedByName}</td>
                    <td>
                      <div className="actions-cell">
                        <a className="btn small" href={api.paymentRequestPdfUrl(r.lotId)}>
                          Договор-заявка (PDF)
                        </a>
                        {tab === "open" && (
                          <button type="button" className="btn small primary" onClick={() => setPaying(r)}>
                            Оплачено
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </>
      )}

      {paying && (
        <PaidModal
          row={paying}
          onClose={() => setPaying(null)}
          onDone={() => {
            setPaying(null);
            void load();
          }}
        />
      )}
    </section>
  );
}
